"use client";

import { Card, CardFooter, CardHeader } from "@/components/ui/card";
import { FaLightbulb } from "react-icons/fa6";
import { Layers2, Eye } from "lucide-react";
import { formatToDecimalPlaces } from "@/helpers/helperFunctions";
import { useDarkTheme } from "@/contexts/DarkTheme";
import Marquee from "react-fast-marquee";
import { Label } from "@radix-ui/react-label";
import { cn } from "@/lib/utils";
import { useTranslation } from "next-i18next";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { AssetBadge } from "../common/AssetBadge";

export interface StrategyCardSmallProps {
  item: any;
  index: number;
  logos: Record<string, string>;
  isLoadingTicker: boolean;
  isCurrentPriceLoading: boolean;
  onView: (data: any) => void;
  onExplain: (data: any) => void;
  onCopy: (item: any) => void;
  showExplain: boolean;
}

export default function StrategyCardSmall({
  item,
  index,
  logos,
  isCurrentPriceLoading,
  onView,
  onExplain,
  onCopy,
  showExplain,
}: StrategyCardSmallProps) {
  const { t } = useTranslation("build-page");
  const { details } = useAppDetails();


  const rawCanvas = item?.strategy?.canvas_data;
  const canvasData = rawCanvas ? JSON.parse(rawCanvas) : [];

  const nodes = canvasData?.filter((n: any) => n.type === "selectorNode") ?? [];
  const edges = canvasData?.filter((n: any) => n.type === "buttonedge") ?? [];

  const category = Object.keys(
    item?.meta_data?.strategy_meta?.indicator_category || {},
  ).join(", ");

  let actions = new Set(item?.meta_data?.strategy_meta?.actions);

  return (
    <Card
      key={item?.strategy?.symbol + "_" + index}
      className={cn("h-fit", index !== 0 ? "mt-2" : "")}
    >
      <CardHeader className="px-4 pt-4 pb-2">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Symbol */}
          <div className="flex items-center gap-3">
            {!details?.isWhiteLabled && (
              <AssetBadge
                logo={logos?.[item?.strategy?.symbol]}
                hideLogo={!logos?.[item?.strategy?.symbol]}
              ></AssetBadge>
            )}

            <div>
              <div className="text-sm font-medium">
                {item.strategy.symbol}
                <span className="ml-1 text-xs text-muted-foreground">
                  {item.strategy.timeframe}
                </span>
              </div>

              {category && (
                <div className="bg-[--accent-primary] rounded-xl text-xs p-[1px] px-2 mt-2  text-white max-w-fit">
                  {category}
                </div>
              )}
            </div>
          </div>

          {/* Right: Win % */}
          <div className="text-right">
            <div
              className={cn(
                "text-2xl font-semibold",
                item?.backtest_data?.win >= 75
                  ? "text-[--marketplace-green]"
                  : item?.backtest_data?.win >= 50
                    ? "text-[--accent-secondary]"
                    : "text-[--marketplace-red]",
              )}
            >
              {formatToDecimalPlaces(item?.backtest_data?.win, 0)}%
            </div>
            <div className="text-[11px] text-muted-foreground">
              {t("Average Win")}
            </div>
          </div>
        </div>
      </CardHeader>

      <CardFooter className="px-4 pb-4 pt-2 flex items-center justify-between">
        {/* Strategy name */}
        <div className="text-muted-foreground font-semibold text-xs max-w-[200px]  overflow-hidden whitespace-nowrap">
          {item?.strategy?.strategy_name?.length > 28 ? (
            <Marquee speed={20} gradient={false}>
              <span className="pr-4">{item?.strategy?.strategy_name}</span>
            </Marquee>
          ) : (
            <Label>{item?.strategy?.strategy_name}</Label>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          {showExplain && (
            <button
              className="flex items-center gap-1 hover:text-foreground transition"
              type="button"
              onClick={() =>
                onExplain({
                  nodes,
                  edges,
                  symbol: item.strategy.symbol,
                  timeframe: item.strategy.timeframe,
                  strategy_id: item?.strategy?.id,
                })
              }
            >
              <FaLightbulb className="w-4 h-4" />
              <span>{t("Explain")}</span>
            </button>
          )}

          <button
            className="flex items-center gap-1 hover:text-foreground transition"
            type="button"
            onClick={(e) => {
              onView({
                nodes,
                edges,
                symbol: item.strategy.symbol,
                timeframe: item.strategy.timeframe,
              });
            }}
          >
            <Eye className="w-4 h-4" />
            <span>{t("View")}</span>
          </button>

          <button
            type="button"
            className="flex items-center gap-1 hover:text-foreground transition"
            onClick={() => onCopy(item)}
          >
            <Layers2 className="w-4 h-4" />
            <span>{t("Copy")}</span>
          </button>
        </div>
      </CardFooter>
    </Card>
  );
}
